/**
 * Geofence schedules (S027): timezone-aware active windows.
 *
 * A schedule's window times are **local to the operating market's IANA timezone**, not UTC — a "peak surge
 * 18:00–20:00" fence means local clock time. Evaluation converts the UTC instant to the market timezone with
 * `Intl` (no hardcoded timezone) and checks the local weekday + time. An absent or empty schedule is always
 * active.
 */
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
/** A window may end at `24:00` (the end of its day); string comparison against `HH:MM` still orders correctly. */
const HHMM_END = /^(?:([01]\d|2[0-3]):[0-5]\d|24:00)$/;
export function validateSchedule(schedule) {
    for (const w of schedule.windows) {
        if (w.days.length === 0)
            return 'BAD_SCHEDULE';
        if (!HHMM.test(w.start) || !HHMM_END.test(w.end))
            return 'BAD_SCHEDULE';
        if (w.start >= w.end)
            return 'BAD_SCHEDULE';
    }
    return null;
}
const WEEKDAY_MAP = {
    Mon: 'mon',
    Tue: 'tue',
    Wed: 'wed',
    Thu: 'thu',
    Fri: 'fri',
    Sat: 'sat',
    Sun: 'sun',
};
const formatters = new Map();
/** One formatter per timezone (constructing an `Intl.DateTimeFormat` dominates the cost of a lookup; the opening scan makes thousands). */
function formatterFor(timeZone) {
    let formatter = formatters.get(timeZone);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
        if (formatters.size > 64)
            formatters.clear();
        formatters.set(timeZone, formatter);
    }
    return formatter;
}
const zoneValidity = new Map();
/** Whether `timeZone` is an IANA zone the runtime can render (D616 round 3). Never throws; a non-string or empty value is not valid. */
export function isValidTimeZone(timeZone) {
    if (typeof timeZone !== 'string' || timeZone === '')
        return false;
    const known = zoneValidity.get(timeZone);
    if (known !== undefined)
        return known;
    let ok = true;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
    }
    catch {
        ok = false;
    }
    if (zoneValidity.size > 256)
        zoneValidity.clear();
    zoneValidity.set(timeZone, ok);
    return ok;
}
/** Local weekday + `HH:MM` for a UTC instant, rendered in the given IANA timezone. */
export function localParts(instantIso, timeZone) {
    const date = new Date(instantIso);
    const parts = formatterFor(timeZone).formatToParts(date);
    let weekday = 'mon';
    let hour = '00';
    let minute = '00';
    for (const p of parts) {
        if (p.type === 'weekday')
            weekday = WEEKDAY_MAP[p.value] ?? 'mon';
        else if (p.type === 'hour')
            hour = p.value === '24' ? '00' : p.value;
        else if (p.type === 'minute')
            minute = p.value;
    }
    return { weekday, hhmm: `${hour}:${minute}` };
}
/** Whether a schedule is active at a UTC instant, evaluated in the market timezone. Empty schedule = always. */
export function isScheduleActiveAt(schedule, instantIso, timeZone) {
    if (schedule === undefined || schedule.windows.length === 0)
        return true;
    const { weekday, hhmm } = localParts(instantIso, timeZone);
    return schedule.windows.some((w) => w.days.includes(weekday) && w.start <= hhmm && hhmm < w.end);
}
//# sourceMappingURL=schedule.js.map